import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { donationAPI } from "../services/api";
import { RevealUp, RevealLeft, RevealRight, RevealScale } from "../components/ScrollReveal";

const RAZORPAY_URL = "https://pages.razorpay.com/pl_SnCmyAkUDcUSwf/view";
const PURPOSES = ["सामान्य दान","मंदिर निर्माण","भंडारा आयोजन","गरीब सहायता","शिक्षा सहायता","चिकित्सा सहायता","त्योहार आयोजन"];
const AMOUNTS  = [501, 1001, 2100, 5100, 11000, 21000];

/* ── STEP INDICATOR ── */
const Step = ({ n, label, active, done }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
      done  ? "bg-green-500 border-green-500 text-white" :
      active? "border-temple-primary text-temple-primary bg-white" :
              "border-gray-200 text-gray-300 bg-white"}`}>
      {done ? "✓" : n}
    </div>
    <span className={`text-[10px] font-bold whitespace-nowrap ${active?"text-temple-primary":done?"text-green-600":"text-gray-300"}`}>{label}</span>
  </div>
);
const Connector = ({ done }) => (
  <div className={`flex-1 h-0.5 mb-4 transition-all ${done?"bg-green-400":"bg-gray-200"}`} />
);

/* ── MAIN COMPONENT ── */
const Donate = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1=form 2=payment 3=confirm 4=receipt
  const [purpose, setPurpose]   = useState("सामान्य दान");
  const [amount,  setAmount]    = useState("");
  const [message, setMessage]   = useState("");
  const [panNum,  setPanNum]    = useState("");
  const [razorRef,setRazorRef]  = useState("");
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState("");
  const [donation, setDonation] = useState(null); // response from /initiate

  /* STEP 1 → 2: record in DB then open Razorpay */
  const handleInitiate = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) < 1) { setError("कृपया दान राशि दर्ज करें।"); return; }
    setLoading(true); setError("");
    try {
      const res = await donationAPI.initiate({ amount: Number(amount), purpose, message, panNumber: panNum });
      setDonation(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || "त्रुटि हुई। पुनः प्रयास करें।");
    } finally { setLoading(false); }
  };

  /* STEP 2 → 3: open Razorpay, user marks done */
  const openRazorpay = () => {
    window.open(RAZORPAY_URL, "_blank", "noopener,noreferrer");
    setStep(3);
  };

  /* STEP 3 → 4: confirm in DB */
  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await donationAPI.confirm(donation.donationId, { razorpayReference: razorRef, panNumber: panNum });
      setDonation(res.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.message || "पुष्टि में त्रुटि हुई।");
    } finally { setLoading(false); }
  };

  /* ── NOT LOGGED IN: show login wall ── */
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background:"#FEF6E4" }}>
        <RevealScale className="max-w-md w-full bg-white rounded-3xl p-8 shadow-temple-lg border border-temple-secondary/15 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="font-serif text-2xl font-bold text-temple-text mb-3">लॉगिन आवश्यक है</h2>
          <div className="section-divider mb-4" />
          <p className="text-sm text-temple-text-muted mb-6 leading-relaxed">
            दान करने के लिए कृपया पहले लॉगिन करें। लॉगिन के बाद आपकी दान जानकारी और रसीद आपके खाते में सुरक्षित रहेगी।
          </p>
          <div className="space-y-3">
            <Link to="/login" state={{ from: "/donate" }}
              className="block w-full py-3.5 rounded-2xl font-bold text-white text-sm"
              style={{ background:"linear-gradient(135deg,#E8621A,#C44E0E)", boxShadow:"0 4px 15px rgba(232,98,26,.35)" }}>
              🙏 लॉगिन करें
            </Link>
            <Link to="/signup" state={{ from: "/donate" }}
              className="block w-full py-3.5 rounded-2xl font-bold text-sm border-2 border-temple-primary text-temple-primary hover:bg-temple-primary hover:text-white transition-all">
              नया खाता बनाएं
            </Link>
          </div>
          <p className="text-xs text-temple-text-muted mt-4">
            लॉगिन के बाद आप Razorpay से सुरक्षित दान कर सकते हैं।
          </p>
        </RevealScale>
      </div>
    );
  }

  /* ── STEP 4: RECEIPT ── */
  if (step === 4) {
    const d = donation?.donation || donation;
    const receipt = d?.receiptNumber || donation?.receiptNumber;
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background:"#FEF6E4" }}>
        <RevealScale className="max-w-lg w-full">
          <div className="bg-white rounded-3xl shadow-temple-lg overflow-hidden border border-temple-secondary/15">
            {/* Header */}
            <div className="p-6 text-center" style={{ background:"linear-gradient(135deg,#1E5B47,#2D7A60)" }}>
              <div className="text-5xl mb-2">✅</div>
              <h2 className="font-serif text-2xl font-bold text-white">दान सफल!</h2>
              <p className="text-white/75 text-sm mt-1">आपका सहयोग प्राप्त हुआ। धन्यवाद 🙏</p>
            </div>
            {/* Receipt */}
            <div className="p-6 space-y-3">
              <h3 className="font-bold text-temple-text text-center mb-4">दान रसीद</h3>
              {[
                { label:"रसीद संख्या",  value: receipt || "—" },
                { label:"दाता का नाम",  value: user.name },
                { label:"ईमेल",         value: user.email },
                { label:"राशि",         value: `₹${Number(amount).toLocaleString("hi-IN")}` },
                { label:"उद्देश्य",     value: purpose },
                { label:"भुगतान विधि",  value: "Razorpay" },
                { label:"स्थिति",       value: "✅ पूर्ण" },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center py-2 border-b last:border-0"
                  style={{ borderColor:"rgba(244,165,0,.12)" }}>
                  <span className="text-xs font-bold text-temple-text-muted uppercase tracking-wide">{label}</span>
                  <span className="text-sm font-semibold text-temple-text text-right">{value}</span>
                </div>
              ))}
              {razorRef && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs font-bold text-temple-text-muted uppercase tracking-wide">Razorpay Ref</span>
                  <span className="text-xs font-mono text-temple-text">{razorRef}</span>
                </div>
              )}
            </div>
            <div className="px-6 pb-6 space-y-3">
              <p className="text-[10px] text-temple-text-muted text-center">
                80G प्रमाण पत्र के लिए: shreesitaramjimandir@gmail.com
              </p>
              <Link to="/dashboard"
                className="block w-full py-3.5 text-center rounded-2xl font-bold text-white text-sm"
                style={{ background:"linear-gradient(135deg,#E8621A,#C44E0E)" }}>
                डैशबोर्ड पर जाएं →
              </Link>
              <button onClick={() => { setStep(1); setAmount(""); setRazorRef(""); setDonation(null); }}
                className="block w-full py-3 text-center rounded-2xl text-sm font-bold border-2 border-temple-primary text-temple-primary hover:bg-temple-primary hover:text-white transition-all">
                एक और दान करें
              </button>
            </div>
          </div>
        </RevealScale>
      </div>
    );
  }

  /* ── MAIN DONATE PAGE ── */
  return (
    <div className="min-h-screen" style={{ background:"#FEF6E4" }}>

      {/* HERO */}
      <section className="py-16 sm:py-24 relative overflow-hidden"
        style={{ background:"linear-gradient(160deg,#2C1408 0%,#8B1A1A 45%,#C44E0E 80%,#F4A500 100%)" }}>
        <div className="absolute inset-0 opacity-[0.04] flex items-center justify-center pointer-events-none">
          <span style={{ fontSize:"70vw",lineHeight:1 }}>🕉️</span>
        </div>
        <RevealUp className="relative max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color:"rgba(255,190,46,.7)" }}>सुरक्षित दान</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-3">दान एवं सहयोग</h1>
          <p className="italic mb-4" style={{ color:"rgba(255,255,255,.7)" }}>"दानं धर्मस्य मूलम्"</p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background:"rgba(30,91,71,.3)", color:"#4ADE80", border:"1px solid rgba(30,91,71,.4)" }}>
            👤 {user.name} — लॉगिन है
          </div>
        </RevealUp>
      </section>

      {/* STEP WIZARD */}
      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Progress bar */}
        <div className="flex items-center mb-8 px-2">
          <Step n={1} label="विवरण"  active={step===1} done={step>1} />
          <Connector done={step>1} />
          <Step n={2} label="भुगतान" active={step===2} done={step>2} />
          <Connector done={step>2} />
          <Step n={3} label="पुष्टि"  active={step===3} done={step>3} />
          <Connector done={step>3} />
          <Step n={4} label="रसीद"   active={step===4} done={false}  />
        </div>

        <div className="bg-white rounded-3xl shadow-temple-lg overflow-hidden border border-temple-secondary/15">

          {/* Card Header */}
          <div className="p-5 text-white text-center"
            style={{ background:"linear-gradient(135deg,#8B1A1A,#C44E0E)" }}>
            <div className="text-3xl mb-1">
              {step===1?"📝":step===2?"💳":step===3?"✔️":"🎉"}
            </div>
            <h2 className="font-serif text-xl font-bold">
              {step===1?"दान विवरण भरें":step===2?"Razorpay से भुगतान":step===3?"भुगतान की पुष्टि":"रसीद तैयार है"}
            </h2>
          </div>

          {/* ── STEP 1: FORM ── */}
          {step === 1 && (
            <form onSubmit={handleInitiate} className="p-6 space-y-5">
              {/* User info (auto-filled) */}
              <div className="p-4 rounded-2xl" style={{ background:"rgba(30,91,71,.06)", border:"1px solid rgba(30,91,71,.15)" }}>
                <p className="text-xs font-bold text-temple-accent mb-2">👤 आपकी जानकारी (स्वतः भरी)</p>
                <p className="text-sm font-semibold text-temple-text">{user.name}</p>
                <p className="text-xs text-temple-text-muted">{user.email}</p>
              </div>

              {/* Amount presets */}
              <div>
                <label className="block text-xs font-bold text-temple-text mb-2">राशि चुनें (₹) *</label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {AMOUNTS.map(a => (
                    <button key={a} type="button"
                      onClick={() => setAmount(a.toString())}
                      className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${
                        amount===a.toString()
                          ? "text-white border-transparent"
                          : "border-gray-200 text-temple-text hover:border-temple-primary/40"
                      }`}
                      style={amount===a.toString()?{background:"linear-gradient(135deg,#E8621A,#C44E0E)"}:{}}>
                      ₹{a.toLocaleString("hi-IN")}
                    </button>
                  ))}
                </div>
                <input type="number" min="1" value={amount} onChange={e=>{setAmount(e.target.value);setError("");}}
                  placeholder="या अन्य राशि दर्ज करें" className="form-input text-sm" required />
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-xs font-bold text-temple-text mb-2">दान का उद्देश्य</label>
                <select value={purpose} onChange={e=>setPurpose(e.target.value)} className="form-input text-sm">
                  {PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {/* PAN (optional) */}
              <div>
                <label className="block text-xs font-bold text-temple-text mb-2">PAN नंबर (80G के लिए, वैकल्पिक)</label>
                <input type="text" value={panNum} onChange={e=>setPanNum(e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F" maxLength={10} className="form-input text-sm font-mono" />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-bold text-temple-text mb-2">संदेश (वैकल्पिक)</label>
                <textarea rows={2} value={message} onChange={e=>setMessage(e.target.value)}
                  placeholder="कोई विशेष निवेदन..." className="form-input text-sm resize-none" />
              </div>

              {error && (
                <p className="text-xs text-red-600 font-semibold p-3 rounded-xl bg-red-50 border border-red-200">⚠️ {error}</p>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-4 rounded-2xl font-bold text-base text-white disabled:opacity-60"
                style={{ background:"linear-gradient(135deg,#8B1A1A,#C44E0E,#F4A500)", boxShadow:"0 6px 20px rgba(139,26,26,.4)" }}>
                {loading ? <span className="flex items-center justify-center gap-2"><div className="loader"/>जानकारी सहेजी जा रही है...</span>
                  : "आगे बढ़ें — भुगतान करें →"}
              </button>
            </form>
          )}

          {/* ── STEP 2: OPEN RAZORPAY ── */}
          {step === 2 && (
            <div className="p-6 space-y-5 text-center">
              <div className="p-4 rounded-2xl" style={{ background:"rgba(244,165,0,.08)", border:"1px solid rgba(244,165,0,.2)" }}>
                <p className="text-xs font-bold text-temple-text-muted mb-1">दान रिकॉर्ड सहेजा गया</p>
                <p className="font-mono text-sm font-bold text-temple-primary">{donation?.receiptNumber}</p>
                <p className="text-lg font-bold text-temple-text mt-2">₹{Number(amount).toLocaleString("hi-IN")}</p>
                <p className="text-xs text-temple-text-muted">{purpose}</p>
              </div>

              <p className="text-sm text-temple-text-muted leading-relaxed">
                नीचे बटन दबाएं — Razorpay का पेज खुलेगा। वहाँ भुगतान पूरा करें, फिर वापस आकर <strong>"भुगतान हो गया"</strong> बटन दबाएं।
              </p>

              <button onClick={openRazorpay}
                className="w-full py-4 rounded-2xl font-black text-lg text-white transition-all hover:scale-[1.02]"
                style={{ background:"linear-gradient(135deg,#072654,#1460CC)", boxShadow:"0 8px 30px rgba(20,96,204,.45)" }}>
                💳 Razorpay पर जाएं
              </button>

              <button onClick={() => setStep(3)}
                className="w-full py-3 rounded-2xl text-sm font-bold border-2 border-green-500 text-green-600 hover:bg-green-50 transition-all">
                ✅ भुगतान हो गया — पुष्टि करें
              </button>

              <button onClick={() => setStep(1)}
                className="text-xs text-temple-text-muted hover:text-temple-primary underline">
                ← वापस जाएं
              </button>
            </div>
          )}

          {/* ── STEP 3: CONFIRM ── */}
          {step === 3 && (
            <form onSubmit={handleConfirm} className="p-6 space-y-5">
              <p className="text-sm text-temple-text-muted leading-relaxed text-center">
                Razorpay पर भुगतान पूरा होने के बाद, Transaction ID (वैकल्पिक) दर्ज करें और पुष्टि करें।
              </p>

              <div className="p-4 rounded-2xl text-center" style={{ background:"rgba(244,165,0,.08)", border:"1px solid rgba(244,165,0,.2)" }}>
                <p className="text-xs text-temple-text-muted">दान राशि</p>
                <p className="text-2xl font-bold text-temple-primary">₹{Number(amount).toLocaleString("hi-IN")}</p>
                <p className="text-xs text-temple-text-muted mt-1">रसीद: {donation?.receiptNumber}</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-temple-text mb-2">
                  Razorpay Transaction ID / Reference (वैकल्पिक)
                </label>
                <input type="text" value={razorRef} onChange={e=>setRazorRef(e.target.value)}
                  placeholder="pay_XXXXXXXX या Transaction ID"
                  className="form-input text-sm font-mono" />
                <p className="text-[10px] text-temple-text-muted mt-1">
                  Razorpay success page पर दिखता है — भविष्य में रसीद के लिए उपयोगी
                </p>
              </div>

              {panNum === "" && (
                <div>
                  <label className="block text-xs font-bold text-temple-text mb-2">PAN नंबर (80G के लिए)</label>
                  <input type="text" value={panNum} onChange={e=>setPanNum(e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F" maxLength={10} className="form-input text-sm font-mono" />
                </div>
              )}

              {error && (
                <p className="text-xs text-red-600 font-semibold p-3 rounded-xl bg-red-50 border border-red-200">⚠️ {error}</p>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-4 rounded-2xl font-bold text-base text-white disabled:opacity-60"
                style={{ background:"linear-gradient(135deg,#1E5B47,#2D7A60)", boxShadow:"0 6px 20px rgba(30,91,71,.4)" }}>
                {loading
                  ? <span className="flex items-center justify-center gap-2"><div className="loader"/>पुष्टि हो रही है...</span>
                  : "✅ दान की पुष्टि करें"}
              </button>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)}
                  className="flex-1 py-2.5 rounded-xl text-xs font-bold border-2 border-gray-200 text-temple-text-muted hover:border-temple-primary/30 transition-all">
                  ← Razorpay पर वापस
                </button>
                <a href="https://wa.me/919470820521?text=नमस्ते%2C%20मुझे%20दान%20में%20सहायता%20चाहिए।"
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 py-2.5 rounded-xl text-xs font-bold text-center text-white"
                  style={{ background:"linear-gradient(135deg,#128C7E,#25D366)" }}>
                  💬 WhatsApp सहायता
                </a>
              </div>
            </form>
          )}
        </div>

        {/* Trust note */}
        <div className="mt-6 p-4 rounded-2xl text-center text-xs"
          style={{ background:"rgba(232,98,26,.05)", border:"1px solid rgba(244,165,0,.15)", color:"#7A5540" }}>
          🔒 आपकी जानकारी सुरक्षित है · 80G कर छूट पात्र · Trust No: 746
        </div>
      </div>
    </div>
  );
};

export default Donate;
